import { Tag } from 'features/assetDatabase'
import { getNominalThicknessStringForComponent } from 'features/assetDatabase/utils/assetDBNominalHelpers'
import { useGeckoLocale } from 'features/locale'
import React, { useMemo } from 'react'
import { useGeckoTheme } from 'styles/GeckoTheme'
import {
  GeckoAssetModel,
  GeckoDome,
  GeckoDomeWall,
  GeckoFlatCircularFloor,
  GeckoHorizontalTankShell,
  GeckoLaunchTube,
  GeckoLegacyBoilerWalls,
  GeckoMeasurementUnit,
  GeckoPipeSection,
  GeckoTankFlatRoof,
  GeckoTankFloor,
  GeckoVerticalTankFoundation,
  GeckoVerticalTankShell,
} from 'types'

interface AssetDetailsTagsProps {
  assetModels: readonly GeckoAssetModel[]
  unitId: string
  componentId: string
}

type GeckoComponentsWithHeight =
  | GeckoLaunchTube
  | GeckoVerticalTankShell
  | GeckoLegacyBoilerWalls
  | GeckoVerticalTankFoundation

type GeckoComponentsWithRadius =
  | GeckoHorizontalTankShell
  | GeckoVerticalTankShell
  | GeckoTankFlatRoof
  | GeckoTankFloor
  | GeckoPipeSection
  | GeckoLaunchTube
  | GeckoDome
  | GeckoDomeWall
  | GeckoFlatCircularFloor
  | GeckoVerticalTankFoundation

export const AssetDetailsTags = (props: AssetDetailsTagsProps) => {
  const { theme } = useGeckoTheme()
  const { assetModels, unitId, componentId } = props
  const { measurementSystem } = useGeckoLocale()

  const assetModel = assetModels.filter(
    (assetModel) => assetModel.tags.unit === unitId
  )
  const assetModelComponent = assetModel?.[0]?.data?.components.get(componentId)
  const assetModelComponentHeight = assetModelComponent
    ? (assetModelComponent as GeckoComponentsWithHeight).height
    : undefined

  const assetModelComponentRadius = assetModelComponent
    ? (assetModelComponent as GeckoComponentsWithRadius).radius
    : undefined

  const maxNominalThicknessString = useMemo(() => {
    return getNominalThicknessStringForComponent(
      assetModelComponent,
      'max',
      measurementSystem
    )
  }, [assetModelComponent, measurementSystem])

  const minNominalThicknessString = useMemo(() => {
    return getNominalThicknessStringForComponent(
      assetModelComponent,
      'min',
      measurementSystem
    )
  }, [assetModelComponent, measurementSystem])

  return (
    <>
      {assetModelComponentHeight && (
        <Tag $background={theme.common.badge.background.blue}>
          Height:{' '}
          {assetModelComponentHeight
            .convertTo(
              measurementSystem === 'imperial'
                ? GeckoMeasurementUnit.Feet
                : GeckoMeasurementUnit.Meters
            )
            .toString()}
        </Tag>
      )}
      {assetModelComponentRadius && (
        <Tag $background={theme.common.badge.background.green}>
          Diameter:{' '}
          {assetModelComponentRadius
            .scale(2)
            .convertTo(
              measurementSystem === 'imperial'
                ? GeckoMeasurementUnit.Feet
                : GeckoMeasurementUnit.Meters
            )
            .toString()}
        </Tag>
      )}
      {minNominalThicknessString &&
      maxNominalThicknessString &&
      minNominalThicknessString === maxNominalThicknessString ? (
        <Tag $background={theme.common.badge.background.orange}>
          Nominal Thickness: {minNominalThicknessString}
        </Tag>
      ) : (
        <>
          {minNominalThicknessString && (
            <Tag $background={theme.common.badge.background.orange}>
              Min Nominal: {minNominalThicknessString}
            </Tag>
          )}
          {maxNominalThicknessString && (
            <Tag $background={theme.common.badge.background.orange}>
              Max Nominal: {maxNominalThicknessString}
            </Tag>
          )}
        </>
      )}
    </>
  )
}
